// Просмотренные сюжет-ролики — dg_story_v1 (локально, как dg_prefs_v1):
// управляет ТОЛЬКО авто-показом в PlayScreen (показать ролик один раз) и бейджем
// «новое» в коллекции. Анлок/пересмотр считаются из прогресса (meta/story.ts),
// поэтому потеря localStorage ничего не запирает — ролики просто сыграют снова.

import { useSyncExternalStore } from 'react';
import { createPersistedStore } from '@/meta/persistedStore';

interface StoryBlob { watched: string[] }

// В памяти — Set, в сторадже — { watched: string[] }: формат dg_story_v1 не меняем.
function sanitize(raw: unknown): Set<string> {
  const watched = (raw as Partial<StoryBlob> | undefined)?.watched;
  return new Set(Array.isArray(watched)
    ? watched.filter((id): id is string => typeof id === 'string')
    : []);
}

const store = createPersistedStore<Set<string>>('dg_story_v1', sanitize, {
  encode: (set) => JSON.stringify({ watched: [...set] }),
});

export function isStoryWatched(episodeId: string): boolean {
  return store.get().has(episodeId);
}

export function markStoryWatched(episodeId: string): void {
  const cur = store.get();
  if (cur.has(episodeId)) return;
  store.set(new Set(cur).add(episodeId));
}

// Эпизоды, чьё видео не загрузилось в ЭТОЙ сессии (память процесса, не
// localStorage): без этого битый/незалитый файл зацикливал бы показ на каждом
// перезапуске уровня. Перезагрузка страницы пробует снова.
const failed = new Set<string>();

export function markStoryFailed(episodeId: string): void { failed.add(episodeId); }
export function isStoryFailed(episodeId: string): boolean { return failed.has(episodeId); }

/** Тесты/дев-чит: полный сброс просмотренного и сессионных фейлов. */
export function resetStoryWatched(): void {
  failed.clear();
  store.reset();
}

/** Реактивная версия для экранов (бейдж «новое» в коллекции): снапшот —
 *  счётчик правок стора, сам Set в снапшот не годится (мутабельная семантика). */
export function useStoryWatched(): { version: number; isWatched: (id: string) => boolean } {
  const v = useSyncExternalStore(store.subscribe, store.version, store.version);
  return { version: v, isWatched: isStoryWatched };
}
